/**
 * ScheduleControl — per-company rescan schedule chip + inline editor.
 * Uses the existing /api/scheduler/schedules endpoints.
 *
 * Read-only for users without `scan:run`. Quiet chip; full editor in a popover.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import GlassSelect from './GlassSelect';
import { formatBackendDetail } from '../lib/apiErrors';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FREQUENCY_OPTIONS = [
  { hours: 24,  label: 'Daily' },
  { hours: 72,  label: 'Every 3 days' },
  { hours: 168, label: 'Weekly' },
  { hours: 336, label: 'Every 2 weeks' },
  { hours: 720, label: 'Monthly' },
];

function labelFor(hours) {
  const match = FREQUENCY_OPTIONS.find((f) => f.hours === hours);
  return match ? match.label : `Every ${hours}h`;
}

function formatNext(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const days = Math.round((d - Date.now()) / 86400000);
  if (days <= 0) return 'soon';
  if (days === 1) return 'tomorrow';
  return `in ${days}d`;
}

export default function ScheduleControl({ companyId, companyName, onChange }) {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('scan:run');
  const [schedule, setSchedule] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const popoverRef = useRef(null);

  // Local form state
  const [freq, setFreq] = useState(168);
  const [enabled, setEnabled] = useState(true);
  const [confirmingClear, setConfirmingClear] = useState(false);

  const load = async () => {
    try {
      const res = await fetch(`${API_URL}/api/scheduler/schedules?company_id=${companyId}`);
      if (!res.ok) return;
      const list = await res.json();
      const active = (list || [])[0] || null;
      setSchedule(active);
      if (active) {
        setFreq(active.frequency_hours);
        setEnabled(!!active.enabled);
      }
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, [companyId]);

  // Close popover on outside click.
  // GlassSelect renders its open panel via a React portal into
  // document.body, so clicks on a cadence option are NOT inside
  // `popoverRef.current`. Treating those as outside clicks would
  // close the popover on mousedown, unmounting GlassSelect before
  // its option `click` fires — the cadence change would be lost and
  // the Update button would stay disabled. Exempt any click inside
  // a `qf-glass-select-panel` (and any future portaled overlay with
  // the `[data-portal-interactive="true"]` marker).
  useEffect(() => {
    if (!open) {
      setConfirmingClear(false);
      return undefined;
    }
    const handler = (e) => {
      if (popoverRef.current && popoverRef.current.contains(e.target)) return;
      if (e.target && typeof e.target.closest === 'function') {
        if (e.target.closest('.qf-glass-select-panel')) return;
        if (e.target.closest('[data-portal-interactive="true"]')) return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const saveNew = async () => {
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${API_URL}/api/scheduler/schedules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_id: companyId, company_name: companyName, frequency_hours: freq,
        }),
      });
      if (!res.ok) throw new Error(formatBackendDetail((await res.json().catch(() => ({}))).detail) || 'Failed to save');
      await load();
      setOpen(false);
      onChange && onChange();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const saveUpdate = async (patch) => {
    if (!schedule) return;
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${API_URL}/api/scheduler/schedules/${schedule.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error(formatBackendDetail((await res.json().catch(() => ({}))).detail) || 'Failed to update');
      await load();
      onChange && onChange();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const clearSchedule = async () => {
    if (!schedule) return;
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${API_URL}/api/scheduler/schedules/${schedule.id}`, { method: 'DELETE' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(formatBackendDetail(body.detail) || 'Failed to remove');
      }
      setSchedule(null);
      setConfirmingClear(false);
      setOpen(false);
      onChange && onChange();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const chip = useMemo(() => {
    if (!schedule) return { text: 'Off', active: false };
    if (!schedule.enabled) return { text: `Paused · ${labelFor(schedule.frequency_hours)}`, active: false };
    return { text: `${labelFor(schedule.frequency_hours)} · ${formatNext(schedule.next_run)}`, active: true };
  }, [schedule]);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} data-testid={`schedule-control-${companyId}`}>
      <button
        type="button"
        onClick={() => canManage && setOpen((o) => !o)}
        disabled={!canManage && !schedule}
        className="btn btn-outline btn-sm"
        style={{
          fontSize: 10, padding: '2px 8px', letterSpacing: '0.02em',
          cursor: canManage ? 'pointer' : 'default',
          color: chip.active ? 'var(--success, #16a34a)' : 'var(--text-secondary)',
          borderColor: chip.active ? 'var(--success, #16a34a)' : 'var(--border)',
        }}
        data-testid={`schedule-chip-${companyId}`}
        title={canManage ? 'Click to edit scan schedule' : 'Scan schedule (read-only)'}
      >
        {chip.active ? '●' : '○'} {chip.text}
      </button>

      {open && canManage && (
        <div
          ref={popoverRef}
          data-testid={`schedule-popover-${companyId}`}
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 20,
            background: 'var(--bg)', border: '1px solid var(--border)',
            borderRadius: 8, padding: 12, minWidth: 230,
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.1)',
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 6 }}>
            Scan schedule
          </div>

          <label style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 3 }}>Frequency</label>
          <GlassSelect
            value={freq}
            onChange={(e) => setFreq(Number(e.target.value))}
            data-testid={`schedule-frequency-${companyId}`}
            style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 12, marginBottom: 10 }}
          >
            {FREQUENCY_OPTIONS.map((f) => (
              <option key={f.hours} value={f.hours}>{f.label}</option>
            ))}
          </GlassSelect>

          {schedule && (
            <>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 10, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={async (e) => { setEnabled(e.target.checked); await saveUpdate({ enabled: e.target.checked }); }}
                  data-testid={`schedule-enabled-${companyId}`}
                />
                <span>Active {enabled ? '(scans will run automatically)' : '(paused)'}</span>
              </label>
              <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginBottom: 10 }}>
                {schedule.last_run ? `Last run: ${new Date(schedule.last_run).toLocaleString()}` : 'Not yet run.'}
                {schedule.next_run && <><br />Next: {new Date(schedule.next_run).toLocaleString()}</>}
                {schedule.last_status && <><br /><em>{schedule.last_status}</em></>}
              </div>
            </>
          )}

          {error && <div style={{ fontSize: 11, color: 'var(--danger)', marginBottom: 6 }}>{error}</div>}

          <div style={{ display: 'flex', gap: 6 }}>
            {schedule ? (
              <>
                <button
                  className="btn btn-primary btn-sm"
                  disabled={busy || freq === schedule.frequency_hours}
                  onClick={() => saveUpdate({ frequency_hours: freq })}
                  data-testid={`schedule-save-${companyId}`}
                  style={{ fontSize: 11, padding: '4px 10px' }}
                >
                  {busy ? 'Saving…' : 'Update'}
                </button>
                {!confirmingClear ? (
                  <button
                    className="btn btn-outline btn-sm"
                    disabled={busy}
                    onClick={() => setConfirmingClear(true)}
                    data-testid={`schedule-clear-${companyId}`}
                    style={{ fontSize: 11, padding: '4px 10px' }}
                  >
                    Turn off
                  </button>
                ) : (
                  <>
                    <button
                      className="btn btn-sm"
                      disabled={busy}
                      onClick={clearSchedule}
                      data-testid={`schedule-confirm-clear-${companyId}`}
                      style={{
                        fontSize: 11, padding: '4px 10px',
                        background: 'var(--danger)', color: 'white', border: '1px solid var(--danger)',
                      }}
                    >
                      {busy ? 'Turning off…' : 'Confirm turn off'}
                    </button>
                    <button
                      className="btn btn-outline btn-sm"
                      disabled={busy}
                      onClick={() => setConfirmingClear(false)}
                      data-testid={`schedule-cancel-clear-${companyId}`}
                      style={{ fontSize: 11, padding: '4px 10px' }}
                    >
                      Cancel
                    </button>
                  </>
                )}
              </>
            ) : (
              <button
                className="btn btn-primary btn-sm"
                disabled={busy}
                onClick={saveNew}
                data-testid={`schedule-create-${companyId}`}
                style={{ fontSize: 11, padding: '4px 10px' }}
              >
                {busy ? 'Scheduling…' : 'Schedule scans'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
